import { type ReactNode } from "react";
import { css } from "styled-system/css";
import { Flex, type FlexProps } from "styled-system/jsx";
import {
  SlideoutDialog,
  type SlideoutDialogProps,
} from "./SlideoutDialog";

const shellClass = css({
  display: "flex",
  flexDirection: "column",
  flex: "1 1 0%",
  w: "full",
  minH: 0,
  overflow: "hidden",
});

const slideoutBodyClass = css({
  // ark-ui form controls (checkbox/radio/switch) render a visually-hidden
  // `position: absolute` <input>. With no positioned ancestor inside the scroll
  // region, that input's containing block becomes the non-scrolling dialog
  // content, so it gets pinned off-screen and decoupled from its visible
  // control. Clicking the control focuses the input, and the browser scrolls
  // the body to reach the off-screen input — the long-standing "footer jumps"
  // bug (most visible on the last checkbox, whose input is pinned furthest
  // down). Making the scroll body the containing block keeps every hidden input
  // aligned with its control, so focus never scrolls the body.
  position: "relative",
  overflowAnchor: "none",
});

const bodyDefaults: FlexProps = {
  flexDir: "column",
  flex: "1 1 0%",
  minH: 0,
  overflowY: "auto",
  overscrollBehavior: "contain",
  className: slideoutBodyClass,
  px: "500",
  pt: "600",
  pb: "400",
};

const footerDefaults: FlexProps = {
  flexShrink: 0,
  px: "500",
  py: "300",
  borderTopWidth: "1px",
  borderColor: "border.light",
  borderStyle: "solid",
  boxShadow: "0px 0px 8px 0px rgba(0, 0, 0, 0.10)",
  justifyContent: "space-between",
  gap: "150",
};

const bannerDefaults: FlexProps = {
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "flex-start",
  width: "full",
  py: "250",
  px: "300",
  gap: "150",
};

export type SlideoutShellProps = {
  /** Main scrollable content. `children` is an alias when `body` is omitted. */
  body?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  banner?: ReactNode;
  bodyProps?: FlexProps;
  footerProps?: FlexProps;
  bannerProps?: FlexProps;
};

/**
 * Flex header/body/footer layout for {@link SlideoutDialog}. The body grows and
 * scrolls; footer/banner are fixed-size siblings, so the footer never collapses
 * upward when body content changes. Prefer {@link SlideoutModal} over trekker
 * `Modal` for right-pane slideouts.
 */
export const SlideoutShell = ({
  body,
  children,
  footer,
  banner,
  bodyProps,
  footerProps,
  bannerProps,
}: SlideoutShellProps) => {
  const bodyContent = body ?? children;

  return (
    <Flex className={shellClass}>
      <Flex {...bodyDefaults} {...bodyProps}>
        {bodyContent}
      </Flex>
      {footer ? (
        <Flex {...footerDefaults} {...footerProps}>
          {footer}
        </Flex>
      ) : null}
      {banner ? (
        <Flex {...bannerDefaults} {...bannerProps}>
          {banner}
        </Flex>
      ) : null}
    </Flex>
  );
};

export type SlideoutModalProps = Omit<
  SlideoutDialogProps,
  "children"
> & {
  body: ReactNode;
  footer?: ReactNode;
  banner?: ReactNode;
  bodyProps?: FlexProps;
  footerProps?: FlexProps;
  bannerProps?: FlexProps;
};

/** Slideout dialog with {@link SlideoutShell} layout baked in. */
export const SlideoutModal = ({
  open,
  body,
  footer,
  banner,
  placement = "rightLargePane",
  bodyProps,
  footerProps,
  bannerProps,
  ...dialogProps
}: SlideoutModalProps) => {
  return (
    <SlideoutDialog open={open} placement={placement} {...dialogProps}>
      <SlideoutShell
        body={body}
        footer={footer}
        banner={banner}
        bodyProps={bodyProps}
        footerProps={footerProps}
        bannerProps={bannerProps}
      />
    </SlideoutDialog>
  );
};
